const initializedReferences = new Set();
const initializationRequests = new Map();
const MAX_INITIALIZATION_ATTEMPTS = 3;
const INITIALIZATION_RETRY_DELAY_MS = 3000;

export default {
  name: "SafaricomPayBillLogicMixin",
  props: {
    pageData: Object,
  },
  data() {
    return {
      repaymentData: {},
      shortCode: "",
      channelOutNo: "",
      hr: undefined,
      min: undefined,
      sec: undefined,
      setTimeoutID: undefined,
      initializationStarted: false,
      initializationAttempt: 0,
      initializationRetryTimer: undefined,
      isDisposed: false,
    };
  },
  computed: {
    currencyCode() {
      return this.repaymentData?.configData?.countryInfoVO?.currencyCode || "KES";
    },
    amount() {
      return this.repaymentData?.paymentData?.amount;
    },
    alreadyInitiated() {
      const status = this.pageData?.paymentData?.checkoutStatus;
      const value = status?.value ?? status;
      return value === "THIRD_RESPONSE_SUCCESS" || Number(value) >= 30;
    },
    platformReference() {
      return this.$route.params.platformReference;
    },
    billReference() {
      return this.channelOutNo || this.repaymentData?.paymentData?.channelOutNo || this.platformReference;
    },
  },
  watch: {
    pageData(value) {
      this.repaymentData = value || {};
      this.startCountdown();
    },
  },
  methods: {
    onCopy(value) {
      if (!value || value === "--") {
        this.$toast("No value to copy");
        return;
      }
      const input = document.createElement("input");
      input.value = value;
      document.body.appendChild(input);
      input.select();
      document.execCommand("Copy");
      input.remove();
      this.$toast(this.$t("text_successful_replication"));
    },
    startCountdown() {
      clearTimeout(this.setTimeoutID);
      const remainTime = Number(this.repaymentData?.remainTime);
      if (!Number.isFinite(remainTime)) {
        return;
      }
      this.updateCountdown(remainTime);
    },
    updateCountdown(remainTime) {
      const safeRemainTime = Math.max(remainTime, 0);
      const totalSeconds = Math.floor(safeRemainTime / 1000);
      const hr = Math.min(Math.floor(totalSeconds / 3600), 99);
      const min = Math.floor((totalSeconds % 3600) / 60);
      const sec = totalSeconds % 60;
      this.hr = String(hr).padStart(2, "0");
      this.min = String(min).padStart(2, "0");
      this.sec = String(sec).padStart(2, "0");

      if (safeRemainTime <= 0) {
        this.$emit("expired");
        return;
      }
      this.repaymentData.remainTime = safeRemainTime - 1000;
      this.setTimeoutID = setTimeout(() => this.updateCountdown(this.repaymentData.remainTime), 1000);
    },
    async loadPayBillDisplay() {
      try {
        const response = await this.$http.channelDisplay(this.platformReference);
        if (response?.code == "200" && response.data?.vaAccount) {
          this.shortCode = response.data.vaAccount;
        }
      } catch (error) {
        // The configured PayBill can be retried on the next page load; global polling continues.
      }
    },
    async initializeCheckout() {
      if (this.initializationStarted || this.isDisposed) {
        return;
      }
      this.initializationStarted = true;
      this.initializationAttempt += 1;
      let initialized = this.alreadyInitiated || initializedReferences.has(this.platformReference);
      if (!initialized) {
        let request = initializationRequests.get(this.platformReference);
        if (!request) {
          request = this.$http.v1SdkSysInitialize({
            platformReference: this.platformReference,
          }).then((response) => {
            if (response?.code == "200") {
              this.channelOutNo = response.data?.channelOutNo || "";
              initializedReferences.add(this.platformReference);
              return true;
            }
            return false;
          }).catch(() => false);
          initializationRequests.set(this.platformReference, request);
        }
        initialized = await request;
        if (initializationRequests.get(this.platformReference) === request) {
          initializationRequests.delete(this.platformReference);
        }
      }
      if (this.isDisposed) {
        return;
      }
      await this.loadPayBillDisplay();
      if (this.isDisposed) {
        return;
      }
      if (initialized) {
        this.$emit("paymentInitiated");
      } else if (this.initializationAttempt < MAX_INITIALIZATION_ATTEMPTS) {
        this.initializationStarted = false;
        this.initializationRetryTimer = setTimeout(() => {
          this.initializationRetryTimer = undefined;
          this.initializeCheckout();
        }, INITIALIZATION_RETRY_DELAY_MS);
      }
    },
  },
  created() {
    this.repaymentData = this.pageData || {};
    this.startCountdown();
    this.initializeCheckout();
  },
  beforeDestroy() {
    this.isDisposed = true;
    clearTimeout(this.setTimeoutID);
    clearTimeout(this.initializationRetryTimer);
  },
};
